import { useCallback } from "react";

import type { PDFViewMode } from "@alaffia-technology-solutions/pdf-viewer";

import { parseAsJson, useQueryState } from "nuqs";
import { z } from "zod";

import type { SidebarTabsKey } from "../../components/PDFViewer";
import type { FilePreviewProps } from "../types";

import { sidebarTabsKeys } from "../../components/PDFViewer";

export const filePreviewAnnotationsSchema = z.object({
  height: z.number().min(0).max(1),
  left: z.number().min(0).max(1),
  top: z.number().min(0).max(1),
  width: z.number().min(0).max(1),
});

export const pdfViewerPreviewSchema = z.object({
  activeAnnotationId: z.string().optional(),
  annotations: z.array(filePreviewAnnotationsSchema).optional(),
  fileId: z.string(),
  highlights: z.array(z.string()).optional(),
  page: z.number().optional(),
  searchQuery: z.string().optional(),
  sidebarTab: z.enum(sidebarTabsKeys).optional(),
  viewMode: z
    .enum(["single-page-view-mode", "continuous-view-mode"])
    .optional(),
});

type PdfViewerPreviewSchema = z.infer<typeof pdfViewerPreviewSchema>;

type PdfViewerPreviewState = PdfViewerPreviewSchema | null;

interface ActiveAnnotationChangedAction {
  type: "pdf-viewer-active-annotation-changed";
  annotationId: string | null;
}
interface SidebarTabChangedAction {
  type: "pdf-viewer-sidebar-tab-changed";
  tab: SidebarTabsKey | null;
}
interface SearchQueryChangedAction {
  type: "pdf-viewer-search-query-changed";
  searchQuery: string;
}
interface PdfViewerOpenedAction {
  type: "pdf-viewer-opened";
  props: FilePreviewProps;
}
interface PdfViewerClosedAction {
  type: "pdf-viewer-closed";
}
interface PageChangedAction {
  type: "pdf-viewer-page-changed";
  pageNumber: number;
}
interface ViewModeChanged {
  type: "pdf-viewer-view-mode-changed";
  viewMode: PDFViewMode;
}

export type PdfViewerPreviewAction =
  | ActiveAnnotationChangedAction
  | SidebarTabChangedAction
  | SearchQueryChangedAction
  | PdfViewerOpenedAction
  | PdfViewerClosedAction
  | PageChangedAction
  | ViewModeChanged;

export const pdfViewerPreviewActions = {
  activeAnnotationChanged: (
    annotationId: string | null,
  ): ActiveAnnotationChangedAction => ({
    type: "pdf-viewer-active-annotation-changed",
    annotationId,
  }),

  sidebarTabChanged: (tab: SidebarTabsKey | null): SidebarTabChangedAction => ({
    type: "pdf-viewer-sidebar-tab-changed",
    tab,
  }),

  searchQueryChanged: (searchQuery: string): SearchQueryChangedAction => ({
    type: "pdf-viewer-search-query-changed",
    searchQuery,
  }),

  pdfViewerClosed: (): PdfViewerClosedAction => ({
    type: "pdf-viewer-closed",
  }),

  pdfViewerOpened: (props: FilePreviewProps): PdfViewerOpenedAction => ({
    type: "pdf-viewer-opened",
    props,
  }),

  pageChanged: (pageNumber: number): PageChangedAction => ({
    type: "pdf-viewer-page-changed",
    pageNumber,
  }),

  viewModeChanged: (viewMode: PDFViewMode): ViewModeChanged => ({
    type: "pdf-viewer-view-mode-changed",
    viewMode,
  }),
};

export const pdfViewerPreviewReducer = (
  state: PdfViewerPreviewState,
  action: PdfViewerPreviewAction,
): PdfViewerPreviewState => {
  switch (action.type) {
    case "pdf-viewer-active-annotation-changed":
      if (!state) return state;

      return {
        ...state,
        activeAnnotationId: action.annotationId ?? undefined,
      };
    case "pdf-viewer-search-query-changed":
      if (!state) return state;
      return {
        ...state,
        searchQuery: action.searchQuery,
      };

    case "pdf-viewer-closed":
      return null;
    case "pdf-viewer-sidebar-tab-changed":
      if (!state) return state;

      return {
        ...state,
        sidebarTab: action.tab ?? undefined,
      };

    case "pdf-viewer-opened":
      return {
        ...action.props,
      };
    case "pdf-viewer-page-changed":
      if (!state) return state;

      return {
        ...state,
        page: action.pageNumber,
      };
    case "pdf-viewer-view-mode-changed":
      if (!state) return state;

      return {
        ...state,
        viewMode: action.viewMode,
      };
  }
};

export const usePDFViewerPreview = () => {
  const [filePreview, setFilePreview] = useQueryState(
    "filePreview",
    // eslint-disable-next-line @typescript-eslint/unbound-method
    parseAsJson(pdfViewerPreviewSchema.parse),
  );

  const dispatchPdfViewerAction = useCallback(
    async (action: PdfViewerPreviewAction) => {
      await setFilePreview((value) => pdfViewerPreviewReducer(value, action));
    },
    [setFilePreview],
  );

  return {
    pdfViewerPreview: filePreview,
    dispatchPdfViewerAction,
  };
};
